import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Calendar,
  Trophy, 
  Users,
  Target,
  Shield,
  Clock,
  Activity,
  CheckCircle2,
  Award,
  Star,
  BarChart3,
  MapPin,
  Play,
  Pause,
  Building
} from 'lucide-react';
import { seasonsApi } from '../../services/api';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import Breadcrumb from '../Common/Breadcrumb';
import { EmptyStates } from '../Common';

interface Season {
  id: string;
  name: string;
  slug?: string;
  start_year: number;
  end_year: number;
  sport?: {
    name: string;
    slug: string;
  };
  league?: {
    name: string;
    slug: string;
  };
  level?: {
    name: string;
    slug: string;
  };
  status: 'completed' | 'active' | 'upcoming';
  start_date: string;
  end_date?: string;
  teams_count?: number;
  games_count?: number;
  championship?: {
    name: string;
    slug: string;
  };
  playoffs_format?: string;
  regular_season_games?: number;
  champion?: {
    team_name: string;
    team_city: string;
    team_abbreviation: string;
    slug?: string;
  };
}

const SeasonDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const siteConfig = useSiteConfig();
  const [activeTab, setActiveTab] = useState<'overview' | 'teams' | 'standings' | 'playoffs'>('overview');

  // Fetch season details
  const { data: season, isLoading, error } = useQuery<Season>({
    queryKey: ['season', slug],
    queryFn: async () => {
      // For now, use getAll and find by slug since there's no getBySlug method yet
      const response = await seasonsApi.getAll();
      const seasons = response.data;
      const found = seasons.find((s: any) => s.slug === slug || s.id === slug);
      if (!found) {
        throw new Error('Season not found');
      }
      return {
        id: found.id,
        name: found.name || `${found.start_year} Season`,
        slug: found.slug,
        start_year: found.start_year,
        end_year: found.end_year,
        sport: found.sport,
        league: found.league,
        level: found.level,
        status: found.is_active ? 'active' : 'completed',
        start_date: found.start_date,
        end_date: found.end_date,
        teams_count: found.teams_count,
        games_count: found.games_count,
        championship: found.championship,
        playoffs_format: found.playoffs_format,
        regular_season_games: found.regular_season_games,
        champion: found.champion
      };
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !season) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card p-12 text-center max-w-md">
          <Calendar className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
          <h1 className="text-2xl font-bold text-white mb-2">Season Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error ? (error as Error).message : 'The season you are looking for does not exist.'}
          </p>
          <Breadcrumb
            items={[{ label: 'All Seasons', path: '/seasons' }]}
            showBackButton={true}
            backButtonText="Back to Seasons"
            backButtonPath="/seasons"
          />
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return Activity;
      case 'completed': return CheckCircle2;
      case 'upcoming': return Clock;
      default: return Calendar;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'completed': return 'text-gray-400';
      case 'upcoming': return 'text-primary';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'completed': return 'badge-secondary';
      case 'upcoming': return 'badge-primary';
      default: return 'badge-secondary';
    }
  };

  const StatusIcon = getStatusIcon(season.status);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'teams', label: 'Teams', icon: Users },
    { id: 'standings', label: 'Standings', icon: BarChart3 },
    { id: 'playoffs', label: 'Playoffs', icon: Trophy },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Breadcrumb
            items={[
              { label: 'All Seasons', path: '/seasons' },
              { label: season?.name || 'Season Details', isActive: true }
            ]}
          />

          <div className="glass-card p-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-center space-x-6">
                {/* Season Icon */}
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 blur-2xl" />
                  <div className="relative w-24 h-24 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                    <StatusIcon className="w-12 h-12 text-primary" />
                  </div>
                </div>

                {/* Season Info */}
                <div>
                  <h1 className="text-4xl font-black text-white mb-2">
                    {season.name}
                  </h1>
                  <div className="flex items-center space-x-4 mb-2">
                    {season.sport?.name && (
                      <span className="badge badge-primary">
                        {season.sport.name}
                      </span>
                    )}
                    <span className={`badge ${getStatusBadge(season.status)}`}>
                      {season.status.charAt(0).toUpperCase() + season.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-400">
                    {season.league?.name} • {season.level?.name}
                  </p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex space-x-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">
                    {season.start_year === season.end_year ? 
                      season.start_year : 
                      `${season.start_year}-${String(season.end_year).slice(-2)}`
                    }
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">
                    Season
                  </div>
                </div>
                {season.teams_count && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-secondary">
                      {season.teams_count}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Teams
                    </div>
                  </div>
                )}
                {season.games_count && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent">
                      {season.games_count}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Games
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Champion Banner */}
            {season.champion && (
              <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20">
                <div className="flex items-center space-x-3">
                  <Trophy className="w-6 h-6 text-primary" />
                  <div>
                    <div className="text-sm text-primary font-semibold uppercase tracking-wider">
                      Season Champion
                    </div>
                    <div className="text-xl font-bold text-white">
                      {season.champion.team_city} {season.champion.team_name}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="glass-card p-1 mb-8">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${activeTab === tab.id
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'text-gray-400 hover:text-white hover:bg-surface/50'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Season Information */}
              <div className="lg:col-span-2 space-y-6">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-primary" />
                    Season Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Start Date
                      </div>
                      <div className="text-white">
                        {new Date(season.start_date).toLocaleDateString()}
                      </div>
                    </div>
                    {season.end_date && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          End Date
                        </div>
                        <div className="text-white">
                          {new Date(season.end_date).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                    {season.sport?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Sport
                        </div>
                        <div className="text-white">{season.sport.name}</div>
                      </div>
                    )}
                    {season.league?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          League
                        </div>
                        <div className="text-white">{season.league.name}</div>
                      </div>
                    )}
                    {season.level?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Level
                        </div>
                        <div className="text-white">{season.level.name}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Status
                      </div>
                      <span className={`badge ${getStatusBadge(season.status)}`}>
                        {season.status.charAt(0).toUpperCase() + season.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Championship Information */}
                {season.championship && (
                  <div className="glass-card p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <Trophy className="w-5 h-5 mr-2 text-secondary" />
                      Championship
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 rounded-lg bg-surface/50 border border-secondary/20">
                        <div className="flex items-center space-x-3">
                          <Award className="w-8 h-8 text-secondary" />
                          <div>
                            <div className="text-white font-semibold">
                              {season.championship.name}
                            </div>
                            <div className="text-sm text-gray-400">
                              Championship Tournament
                            </div>
                          </div>
                        </div>
                      </div>
                      {season.champion && (
                        <div className="p-4 rounded-lg bg-surface/30 border border-border">
                          <div className="text-sm text-gray-400 mb-1">
                            Champion
                          </div>
                          <div className="text-xl font-bold text-white">
                            {season.champion.team_city} {season.champion.team_name}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">
                    Season Stats
                  </h3>
                  <div className="space-y-3">
                    {season.teams_count && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Teams</span>
                        <span className="text-white font-semibold">
                          {season.teams_count}
                        </span>
                      </div>
                    )}
                    {season.games_count && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Total Games</span>
                        <span className="text-white font-semibold">
                          {season.games_count}
                        </span>
                      </div>
                    )}
                    {season.regular_season_games && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Regular Season</span>
                        <span className="text-white font-semibold">
                          {season.regular_season_games} games
                        </span>
                      </div>
                    )}
                    {season.playoffs_format && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Playoff Format</span>
                        <span className="text-white font-semibold">
                          {season.playoffs_format}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'teams' && (
            <motion.div
              key="teams"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass-card p-6"
            >
              <EmptyStates.ComingSoon feature="Team rosters for this season" />
            </motion.div>
          )}

          {activeTab === 'standings' && (
            <motion.div
              key="standings"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass-card p-6"
            >
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Standings Coming Soon</h3>
                <p className="text-gray-400">
                  Season standings will be available soon.
                </p>
              </div>
            </motion.div>
          )}

          {activeTab === 'playoffs' && (
            <motion.div
              key="playoffs"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass-card p-6"
            >
              <div className="text-center py-12">
                <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Playoffs Coming Soon</h3>
                <p className="text-gray-400">
                  Playoff bracket and results will be available soon.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SeasonDetail;